import {Trans} from '@lingui/macro';
import {keyBy} from 'lodash';
import React, {FC, useMemo} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {VirtualTableSortableColumnDirectionsEnum} from '../../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../../components/common/table/staticVirtualTable';
import {SelectedColumnSort} from '../../../components/common/table/tableColumn';
import {TableEmptyState} from '../../../components/common/table/tableEmptyState';
import {VirtualTableRowSiblingData} from '../../../components/common/table/virtualTableBase';
import {SettingsRulesUrl} from '../../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';
import {useComputeRuleTabs} from '../ruleHeader';
import {
  computeSortedColumn,
  isRuleListTableSortEnum,
  RULE_LIST_COLUMNS,
  RuleListItem,
  RuleListTableSortsEnum,
  SORTABLE_COLUMNS,
} from './ruleListHelpers';
import {RuleListMultiSelectHeader} from './ruleListMultiSelectHeader';
import {RuleListRow, RuleListRowActions} from './ruleListRow';

/*
 * Props.
 */

export interface RuleListTableProps extends RuleListRowActions {
  rulesUrl: SettingsRulesUrl;

  items: ReadonlyArray<RuleListItem>;
  selectedItemsIds: ReadonlyArray<number>;

  onLoadMore: Handler;
  moreStatus: AsyncMoreStatusesEnum;

  ruleSort: RuleListTableSortsEnum;
  onRuleSortChange: HandlerOf<RuleListTableSortsEnum>;

  onRuleSelectedToggle: HandlerOf<number>;
  onRuleSelect: HandlerOf<RuleListItem>;

  onBulkDeleteClick: Handler;
  onBulkActivateClick: Handler;
  onBulkDeactivateClick: Handler;
  onHeaderMultiSelect: HandlerOf<boolean>;
}

/*
 * Component.
 */

export const RuleListTable: FC<RuleListTableProps> = ({
  rulesUrl,
  items,
  onLoadMore,
  moreStatus,
  ruleSort,
  onRuleSortChange,
  selectedItemsIds,
  onRuleSelectedToggle,
  onBulkActivateClick,
  onBulkDeactivateClick,
  onBulkDeleteClick,
  onHeaderMultiSelect,
  ...actions
}) => {
  const onColumnClick = (col: SelectedColumnSort) => {
    const newSort = `${col.direction === VirtualTableSortableColumnDirectionsEnum.DESCENDING ? '-' : ''}${
      col.id
    }`;
    if (!isRuleListTableSortEnum(newSort)) {
      return;
    }
    onRuleSortChange(newSort);
  };

  const {hasTabs} = useComputeRuleTabs(rulesUrl);
  const headerOffset = hasTabs ? 254 : 192;

  const itemsIds = useMemo(() => items.map((item) => item.id), [items]);
  const itemIdToRules = useMemo(() => keyBy(items, (item) => item.id), [items]);

  return (
    <StaticVirtualTable<number>
      columns={RULE_LIST_COLUMNS}
      shouldAllowMultiSelect
      sortableColumns={SORTABLE_COLUMNS}
      sortedColumn={computeSortedColumn(ruleSort)}
      items={itemsIds}
      hasData={moreStatus !== AsyncMoreStatusesEnum.NONE}
      shouldRenderEmptyState={moreStatus === AsyncMoreStatusesEnum.ALL_LOADED && items.length === 0}
      renderEmpty={() => (
        <TableEmptyState headerOffset={headerOffset}>
          <Trans id="rule-list-table-empty-results-description">No rules matched your criteria</Trans>
        </TableEmptyState>
      )}
      moreStatuses={{
        [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
        [ScrollDirectionsEnum.BOTTOM]: moreStatus,
      }}
      onSortUpdate={onColumnClick}
      onLoadMore={onLoadMore}
      isInMultiSelectMode={selectedItemsIds.length > 0}
      onMultiSelectHeaderChange={onHeaderMultiSelect}
      renderMultiSelectHeader={(onMultiSelectHeaderChange) => (
        <RuleListMultiSelectHeader
          onMultiSelectChange={onMultiSelectHeaderChange}
          selectedBundlesIds={selectedItemsIds}
          numberOfBundlesInList={items.length}
          onSetActiveClick={onBulkActivateClick}
          onSetInactiveClick={onBulkDeactivateClick}
          onDeleteClick={onBulkDeleteClick}
        />
      )}
      renderRow={(itemId, options, siblingData) => (
        <RuleListRow
          {...computeRowProps(itemId, selectedItemsIds, siblingData)}
          {...actions}
          shouldAllowMultiSelect
          onSelect={(e) => actions.onRuleSelect(itemIdToRules[itemId])}
          onCheckBoxClick={() => onRuleSelectedToggle(itemId)}
          columns={RULE_LIST_COLUMNS}
          options={options}
          rule={itemIdToRules[itemId]}
        />
      )}
    />
  );
};

function computeRowProps(
  itemId: number,
  selectedItems: ReadonlyArray<number>,
  siblingData: VirtualTableRowSiblingData<number> | undefined,
) {
  const isNextSiblingSelected = selectedItems.includes(siblingData?.next ?? -1);
  const isPreviousSiblingSelected = selectedItems.includes(siblingData?.previous ?? -1);

  return {
    shouldRemoveTopBorderRadius: isPreviousSiblingSelected,
    shouldRemoveBottomBorderRadius: isNextSiblingSelected,
    isSelected: selectedItems.includes(itemId),
    isInMultiSelectMode: selectedItems.length > 0,
    isCheckboxesVisible: selectedItems.length > 0,
  };
}
